import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import { formatMixedFractionLatex } from "../../../../utils/fractionLatex";

/*
Typ 2: Ułamki – wspólny mianownik + nawias
Zawsze:  A − (C ± E)
Ułamki: zawsze właściwe i nieskracalne
Odpowiedź: akceptujemy dowolną równoważną liczbowo postać (skr./nieskr., mieszany, niewłaściwy itd.)
*/

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function gcd(a, b) {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b !== 0) {
    const t = a % b;
    a = b;
    b = t;
  }
  return a;
}

function lcm(a, b) {
  return Math.abs((a / gcd(a, b)) * b);
}

function reduceFrac(n, d) {
  if (d < 0) {
    n = -n;
    d = -d;
  }
  const g = gcd(n, d);
  return { n: n / g, d: d / g };
}

function addFrac(A, B) {
  const D = lcm(A.d, B.d);
  const n = A.n * (D / A.d) + B.n * (D / B.d);
  return reduceFrac(n, D);
}

function subFrac(A, B) {
  const D = lcm(A.d, B.d);
  const n = A.n * (D / A.d) - B.n * (D / B.d);
  return reduceFrac(n, D);
}

/** Ładny zapis wyniku:
 * - ujemny: minus przed całością
 * - niewłaściwy: ułamek mieszany
 * - właściwy: zwykły ułamek
 */
const prettyFracLatex = (fraction) =>
  formatMixedFractionLatex(fraction.n, fraction.d);

function fracLatex(F) {
  if (F.d === 1) return `${F.n}`;
  return `\\frac{${F.n}}{${F.d}}`;
}

/** Linia skalowania do wspólnego mianownika:
 *  n/d = (n·k)/(d·k) = (n*k)/D
 */
function scaleLineLatex(n, d, D) {
  const k = D / d;
  if (k === 1) return null; // ✅ nie pokazujemy "·1"
  const nk = n * k;
  return `\\frac{${n}}{${d}}=\\frac{${n}\\cdot${k}}{${d}\\cdot${k}}=\\frac{${nk}}{${D}}`;
}


/** Losuje ułamek właściwy i nieskracalny (licznik < mianownik, gcd=1). */
function randomProperIrreducibleFraction(denPool) {
  const d = pick(denPool);
  // próbujemy kilka razy dobrać licznik, by gcd=1
  for (let i = 0; i < 50; i++) {
    const n = 1 + Math.floor(Math.random() * (d - 1)); // 1..d-1 => zawsze właściwy
    if (gcd(n, d) === 1) return { n, d };
  }
  // awaryjnie: wybierz 1/(cokolwiek)
  return { n: 1, d };
}

export function generateTask() {
  const denPool = [4, 5, 6, 7, 8, 9, 10, 12];

  const A0 = randomProperIrreducibleFraction(denPool);
  const C0 = randomProperIrreducibleFraction(denPool);
  const E0 = randomProperIrreducibleFraction(denPool);

  const A = reduceFrac(A0.n, A0.d);
  const C = reduceFrac(C0.n, C0.d);
  const E = reduceFrac(E0.n, E0.d);

  // Zewnętrznie zawsze odejmowanie, wewnątrz +/-
  const insideOp = pick(["+", "-"]);
  const inside = insideOp === "+" ? addFrac(C, E) : subFrac(C, E);
  const result = subFrac(A, inside);

  const exprLatex = `\\frac{${A.n}}{${A.d}}-\\left(\\frac{${C.n}}{${C.d}}${insideOp}\\frac{${E.n}}{${E.d}}\\right)`;

  return {
    type: "FractionCommonDenominatorOpenTask",
    data: {
      A, C, E,
      insideOp,
      inside,
      result,
      exprLatex
    },
    tags: ["dzialania", "ulamki", "wspolny_mianownik"]
  };
}

export function renderTask({ data }) {
  return (
    <div className="space-y-2" style={{ textAlign: "left" }}>
      <p>
        Oblicz wartość:
        <span style={{ marginLeft: 10 }}>
          <InlineMath math={data.exprLatex} />
        </span>
        .
      </p>
    </div>
  );
}

export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer || ""}
      onChange={onChange} // ✅ trzymamy obiekt {raw, parsed, ...}
      disabled={disabled}
      label=""
      placeholder="np. -1 9/20, -29/20, -58/40..."
      wide={true}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  // ✅ akceptujemy dowolną równoważną liczbowo postać
  const parsed = userAnswer?.parsed;
  if (parsed == null || Number.isNaN(parsed)) return false;

  const expected = data.result.n / data.result.d;
  return Math.abs(parsed - expected) < 1e-9;
}

export function renderAnswer({ data }) {
  // pokazujemy “ładny” wynik (mieszany / minus przed ułamkiem),
  // ale użytkownik może wpisać dowolnie równoważnie
  return <InlineMath math={prettyFracLatex(data.result)} />;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }
  // ✅ zachowuje np. "-1 9/20"
  const latex = formatLatexPreview(userAnswer.raw, "");
  return <InlineMath math={latex} />;
}

export function renderExplanation({ data }) {
  const { A, C, E, insideOp, inside, result } = data;

  // KROK 1: C (±) E
  const D1 = lcm(C.d, E.d);
  const nC1 = C.n * (D1 / C.d);
  const nE1 = E.n * (D1 / E.d);
  const nInsideAtD1 = insideOp === "+" ? (nC1 + nE1) : (nC1 - nE1);

  // KROK 2: A - inside
  const D2 = lcm(A.d, inside.d);
  const nA2 = A.n * (D2 / A.d);
  const nInside2 = inside.n * (D2 / inside.d);
  const nResultAtD2 = nA2 - nInside2;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1.</strong> Najpierw liczymy wyrażenie w nawiasie:
      </div>
      <BlockMath math={`${fracLatex(C)}${insideOp}${fracLatex(E)}`} />

      <div style={{ textAlign: "center", marginTop: 6, opacity: 0.9 }}>
        Wspólny mianownik: <strong>{D1}</strong>
      </div>

      <div style={{ textAlign: "center" }}>
        <BlockMath math={scaleLineLatex(C.n, C.d, D1)} />
        {scaleLineLatex(E.n, E.d, D1) && <BlockMath math={scaleLineLatex(E.n, E.d, D1)} />}
        <BlockMath
          math={`\\frac{${nC1}}{${D1}}${insideOp}\\frac{${nE1}}{${D1}}=\\frac{${nInsideAtD1}}{${D1}}=${fracLatex(inside)}`}
        />
      </div>

      <div>
        <strong>Krok 2.</strong> Teraz odejmujemy cały nawias od pierwszego ułamka:
      </div>
      <BlockMath math={`${fracLatex(A)}-${fracLatex(inside)}`} />

      <div style={{ textAlign: "center", marginTop: 6, opacity: 0.9 }}>
        Wspólny mianownik: <strong>{D2}</strong>
      </div>

      <div style={{ textAlign: "center" }}>
        <BlockMath math={scaleLineLatex(A.n, A.d, D2)} />
        <BlockMath math={scaleLineLatex(inside.n, inside.d, D2)} />
        <BlockMath
          math={`\\frac{${nA2}}{${D2}}-\\frac{${nInside2}}{${D2}}=\\frac{${nResultAtD2}}{${D2}}=${fracLatex(result)}`}
        />
      </div>

      <div>
        <strong>Wynik:</strong> <InlineMath math={prettyFracLatex(result)} />
      </div>
    </div>
  );
}
